import { combineEpics, ofType } from 'redux-observable'
import { EMPTY, of } from 'rxjs'
import { catchError, filter, map, mergeMap, takeUntil } from 'rxjs/operators'

import { COMMON_ERROR } from 'utils/ErrorMessages'
import { URL_CONST } from 'utils/urlConsts'
import { matchURL } from 'utils/urls'

import { FETCH_SETTINGS_REQUEST_ASYNC as FETCH_GROUP_SETTINGS_REQUEST_ASYNC } from 'containers/GroupSettings/Wizard/constants'
import { FETCH_SELECTED_COMPANY_DATA_ASYNC } from 'containers/PartnerOptions/constants'
import { selectCompanyFilter } from 'containers/PartnerOptions/selectors'
import { addToast } from 'containers/Toasts/actions'

import { DATA_UPDATE_ASYNC } from './Editor/constants'
import editorEpics from './Editor/epics'
import logoEpics from './Logo/epics'
import {
  deleteLocaleError,
  deleteLocaleSuccess,
  fetchDataError,
  fetchDataSuccess,
  fetchLocaleError,
  fetchLocaleSuccess,
} from './actions'
import {
  ADD_LANGUAGE,
  ANNOUNCEMENT_SELECT,
  DATA_REQUEST_ASYNC,
  LOCALE_DELETE_ASYNC,
  LOCALE_REQUEST_ASYNC,
} from './constants'

const fetchDataEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(
      FETCH_SELECTED_COMPANY_DATA_ASYNC.SUCCESS,
      DATA_REQUEST_ASYNC.INITIATE,
      LOCALE_DELETE_ASYNC.SUCCESS
    ),
    filter((action) => {
      const isGroupSettings = matchURL(URL_CONST.groupSettings.wizard)
      if (action.type === FETCH_SELECTED_COMPANY_DATA_ASYNC.SUCCESS && isGroupSettings) {
        return false
      }
      if (action.type === FETCH_SELECTED_COMPANY_DATA_ASYNC.SUCCESS)
        return matchURL(URL_CONST.settings.endUserAlerts)
      return true
    }),
    mergeMap(() =>
      api.settings
        .getAnnouncements(
          state$.value.partnerOptions.selectedCompany?.value,
          state$.value.partnerOptions.selectedCompany?.value || state$.value.app.data.id
        )
        .pipe(
          map((response) => fetchDataSuccess(response)),
          catchError((error) => of(epicsErrorHandler(fetchDataError, error))),
          takeUntil(action$.pipe(ofType(DATA_REQUEST_ASYNC.CANCEL)))
        )
    )
  )

// Track last fetched announcement/locale to prevent duplicates
let lastFetchedKey = null

const fetchLocaleEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(
      LOCALE_REQUEST_ASYNC.INITIATE,
      ANNOUNCEMENT_SELECT,
      LOCALE_DELETE_ASYNC.SUCCESS,
      DATA_UPDATE_ASYNC.SUCCESS,
      ADD_LANGUAGE,
      FETCH_SELECTED_COMPANY_DATA_ASYNC.SUCCESS,
      FETCH_GROUP_SETTINGS_REQUEST_ASYNC.SUCCESS
    ),
    filter((action) => {
      if (action.type === FETCH_SELECTED_COMPANY_DATA_ASYNC.SUCCESS) {
        if (matchURL(URL_CONST.groupSettings.wizard)) {
          return false
        }
        return matchURL(URL_CONST.settings.endUserAlerts)
      }
      // Only process FETCH_GROUP_SETTINGS_REQUEST_ASYNC.SUCCESS in group settings wizard
      if (action.type === FETCH_GROUP_SETTINGS_REQUEST_ASYNC.SUCCESS) {
        // Clear the last fetched key to ensure fresh fetch
        lastFetchedKey = null
        return matchURL(URL_CONST.groupSettings.wizard)
      }
      return true
    }),
    mergeMap((action) => {
      const state = state$.value
      const items = state.settings.notifications.enduser.data.items

      // Check if we have items
      if (!items || items.length === 0) {
        return EMPTY
      }

      // Default to current active selection from state
      let activeId = state.settings.notifications.enduser.data.activeIndex
      let localeId = state.settings.notifications.enduser.data.locale.locale_id

      // When changing selection, use the action's itemIndex to avoid race conditions
      if (action.type === ANNOUNCEMENT_SELECT) {
        activeId = action.payload.itemIndex
        if (items?.[activeId]?.locales?.length) {
          localeId = items[activeId].locales[0]
        }
        // Clear last fetched key to ensure the locale is fetched for the new selection
        lastFetchedKey = null
      }

      if (action.type === DATA_UPDATE_ASYNC.SUCCESS) {
        lastFetchedKey = null
      }

      // Ensure we have a valid item at activeId
      if (!items[activeId]) {
        return EMPTY
      }

      const announcementId = items[activeId].id
      const announcementTrigger = items[activeId].trigger
      const isGroupSettings = matchURL(URL_CONST.groupSettings.wizard)
      const companyFilter = selectCompanyFilter(state)?.value

      // Create a unique key for this fetch
      const fetchKey = `${announcementId}-${localeId}-${announcementTrigger}`

      // Skip if we just fetched this exact combination
      if (fetchKey === lastFetchedKey && action.type !== LOCALE_REQUEST_ASYNC.INITIATE) {
        return EMPTY
      }

      lastFetchedKey = fetchKey

      return api.settings
        .getAnnouncementLocale(
          companyFilter,
          announcementId,
          localeId,
          announcementTrigger,
          isGroupSettings
        )
        .pipe(
          map((response) => fetchLocaleSuccess(response)),
          catchError((error) => of(epicsErrorHandler(fetchLocaleError, error)))
        )
    }),
    takeUntil(action$.pipe(ofType(LOCALE_REQUEST_ASYNC.CANCEL)))
  )

const deleteDataEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(LOCALE_DELETE_ASYNC.INITIATE),
    mergeMap((action) =>
      api.settings
        .deleteAnnouncementLocale(
          state$.value.partnerOptions.selectedCompany?.value,
          action.payload.data
        )
        .pipe(
          mergeMap(() =>
            of(deleteLocaleSuccess(action.payload.data), addToast({ message: 'Deleted!' }))
          ),
          catchError((error) =>
            of(
              epicsErrorHandler(deleteLocaleError, error),
              addToast({
                type: 'error',
                message:
                  error.status === 400 ? "Default Notification can't be removed." : COMMON_ERROR,
              })
            )
          ),
          takeUntil(action$.pipe(ofType(LOCALE_DELETE_ASYNC.CANCEL)))
        )
    )
  )

export default combineEpics(
  editorEpics,
  logoEpics,

  fetchDataEpic,
  fetchLocaleEpic,
  deleteDataEpic
)
